import { colors } from '@ardoq/design-tokens';
import { InfoIcon } from '@ardoq/icons';
import { Box, FlexBox } from '@ardoq/layout';
import styled from 'styled-components';

type MaskOverlayProps = {
  message?: string;
  isMessageCentered: boolean;
  isWarning: boolean;
};

const StripedMask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(
    -45deg,
    transparent 0 4px,
    ${colors.borderAction} 4px 8px
  );
  opacity: 50%;
  pointer-events: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const OverlayMessage = styled(Box).attrs({
  width: 'full',
  backgroundColor: 'surfaceInfo',
  padding: 'medium',
})<{ $isWarning: boolean }>`
  position: absolute;
  bottom: 0;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: ${({ $isWarning }) =>
    $isWarning ? colors.surfaceWarning : colors.surfaceInfo};
`;

const UnlinkedWidgetMask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  opacity: 50%;
  pointer-events: none;
`;

const MaskMessage = ({
  message,
  isMessageCentered,
  isWarning,
}: MaskOverlayProps) => {
  return (
    <OverlayMessage $isWarning={isWarning}>
      <FlexBox
        gap="medium"
        height="full"
        width="full"
        justify={isMessageCentered ? 'center' : 'start'}
        align={isMessageCentered ? 'center' : 'start'}
      >
        <InfoIcon color={isWarning ? colors.iconWarning : colors.iconInfo} />
        {message}
      </FlexBox>
    </OverlayMessage>
  );
};

const SupportedUnlinkedWidgetForFiltersMask = ({
  message,
  isMessageCentered,
  isWarning,
}: MaskOverlayProps) => (
  <>
    <UnlinkedWidgetMask />
    {message && (
      <MaskMessage
        message={message}
        isMessageCentered={isMessageCentered}
        isWarning={isWarning}
      />
    )}
  </>
);

const UnsupportedWidgetForFiltersMask = ({
  message,
  isMessageCentered,
  isWarning,
}: MaskOverlayProps) => (
  <>
    <StripedMask />
    {message && (
      <MaskMessage
        message={message}
        isMessageCentered={isMessageCentered}
        isWarning={isWarning}
      />
    )}
  </>
);

export type GlobalFiltersWidgetMaskProps = {
  isWide: boolean;
  isLinkToLinkModeDashboardFilterSupported: boolean;
  isLinkedToLinkModeDashboardFilter: boolean;
  isWarning: boolean;
  maskMessage?: string;
};

export const GlobalFiltersWidgetMask = ({
  isWide,
  isLinkToLinkModeDashboardFilterSupported,
  isLinkedToLinkModeDashboardFilter,
  isWarning,
  maskMessage,
}: GlobalFiltersWidgetMaskProps) =>
  !isLinkToLinkModeDashboardFilterSupported ? (
    <UnsupportedWidgetForFiltersMask
      message={maskMessage}
      isMessageCentered={isWide}
      isWarning={isWarning}
    />
  ) : !isLinkedToLinkModeDashboardFilter ? (
    <SupportedUnlinkedWidgetForFiltersMask
      message={maskMessage}
      isMessageCentered={isWide}
      isWarning={isWarning}
    />
  ) : null;
